import { useState } from "react";
import type { Question, SurveyAnswers } from "../../shared/types";

interface Props {
  intro: string;
  questions: Question[];
  onDone: (answers: SurveyAnswers) => void;
}

/** Short paginated survey: one question per screen. */
export default function Survey({ intro, questions, onDone }: Props) {
  const [i, setI] = useState(0);
  const [answers, setAnswers] = useState<SurveyAnswers>({});
  const q = questions[i];
  const val = answers[q.id];
  const answered = q.answerType === "short_text"
    ? typeof val === "string" && val.trim().length > 0
    : Array.isArray(val) ? val.length > 0 : !!val;
  const canContinue = !q.required || answered;
  const last = i === questions.length - 1;

  const set = (v: string | string[]) => setAnswers((a) => ({ ...a, [q.id]: v }));
  const toggle = (optId: string) => {
    if (q.answerType === "single_select") return set(optId);
    const cur = Array.isArray(val) ? val : [];
    set(cur.includes(optId) ? cur.filter((x) => x !== optId) : [...cur, optId]);
  };
  const advance = () => (last ? onDone(answers) : setI(i + 1));

  return (
    <form className="card-center" onSubmit={(e) => { e.preventDefault(); if (canContinue) advance(); }}>
      {i === 0 && intro && <p className="muted">{intro}</p>}
      <div className="dots" aria-label={`Question ${i + 1} of ${questions.length}`}>
        {questions.map((x, n) => <i key={x.id} className={n <= i ? "on" : ""} />)}
      </div>
      <h2>{q.prompt}</h2>
      {q.answerType === "short_text" ? (
        <input type="text" autoFocus value={typeof val === "string" ? val : ""} onChange={(e) => set(e.target.value)} maxLength={400} />
      ) : (
        <div className="choices" role={q.answerType === "single_select" ? "radiogroup" : "group"}>
          {q.answerType === "multi_select" && <span className="muted small">Choose all that apply.</span>}
          {q.options.map((o) => {
            const on = Array.isArray(val) ? val.includes(o.id) : val === o.id;
            return (
              <button
                key={o.id}
                type="button"
                role={q.answerType === "single_select" ? "radio" : "checkbox"}
                aria-checked={on}
                className={`choice ${q.answerType === "multi_select" ? "multi" : ""} ${on ? "on" : ""}`}
                onClick={() => toggle(o.id)}
              >
                <span className="box" />{o.label}
              </button>
            );
          })}
        </div>
      )}
      <div className="row">
        {i > 0 && <button type="button" className="btn btn-ghost" onClick={() => setI(i - 1)}>Back</button>}
        <span className="spacer" />
        {!q.required && !answered && <button type="button" className="btn btn-ghost" onClick={advance}>Skip</button>}
        <button type="submit" className="btn btn-primary" disabled={!canContinue}>{last ? "Start the demo" : "Continue"}</button>
      </div>
    </form>
  );
}
